import { defineStore } from 'pinia'

// 视频详情项类型
export type VideoDetailItem = {
  isbuy: number;           // 是否购买：0-无需购买，1-需要购买
  numid: number;          // 集数数字
  videourl: string;       // 视频地址
  videotitle: string;     // 每一集的剧情描述
}

// 视频主类型
export type Video = {
  id: string;                      // 视频ID
  name: string;                    // 视频名称
  imgs: string;                    // 封面图片URL
  detailsList: VideoDetailItem[];  // 视频详情列表
  describes: string;               // 视频描述
  iscollect: number;               // 是否收藏：0-未收藏，1-已收藏
  istxt: number;                   // 是否是文本类型：0-否，1-是
}

export type UserInfo = {
  id: string;
  nickname: string;
  token: string;
  coin: number;
  islogin: number;
  isvip: number;
  autolock: number;
  vipleftbtntxt: string;
  language: 'zh-Hant' | 'en';
  allLock: number;
}

export type ProtocolLinks = {
  privacy: string
  user: string
  recharge: string
  rechargeexplain: string
}

const defaultUserInfo = (): UserInfo => ({
  id: '',
  nickname: '',
  token: '',
  coin: 0,
  islogin: 0,
  isvip: 0,
  autolock: 1,
  vipleftbtntxt: '',
  language: 'zh-Hant',
  allLock: 0
})

const defaultProtocolLinks = (): ProtocolLinks => ({
  privacy: '',
  user: '',
  recharge: '',
  rechargeexplain: ''
})

export const useUserStore = defineStore('user', {
  state: () => ({
    uuid: '',
    playing: {} as Record<string, Video>,
    userInfo: defaultUserInfo(),
    protocolLinks: defaultProtocolLinks(),
    isLogin: false,
    quality: 'auto'  // 视频清晰度，auto-自动，normal-标清，high-高清
  }),
  getters: {
    getUserInfo: (state) => state.userInfo,
    getUuid: (state) => state.uuid,
    getPlaying: (state) => state.playing,
    getQuality: (state) => state.quality,
    getProtocolLinks: (state) => state.protocolLinks,
    // 是否是真正登录用户
    isRealLogin: (state) => {
      return Boolean(state.userInfo.id && state.userInfo.token && state.userInfo.islogin === 1)
    },
  },
  actions: {
    setUuid(uuid: string) {
      this.uuid = uuid
    },
      setUserInfo(userInfo: Partial<UserInfo>) {
        this.userInfo = {
          ...this.userInfo,
          ...userInfo
        }
    },
    setProtocolLinks(protocolLinks: Partial<ProtocolLinks>) {
      this.protocolLinks = {
        ...this.protocolLinks,
        ...protocolLinks
      }
    },
    setPlaying(playingData: Video) {
      this.playing[playingData.id] = playingData
    },
    setLoginStatus(status: boolean) {
      this.isLogin = status
    },
    setQuality(quality: string) {
      this.quality = quality
    },
    setLanguage(language: 'zh-Hant' | 'en') {
      this.userInfo = { ...this.userInfo, language }
    },
    setAutoUnlock(enabled: boolean) {
      this.userInfo = { ...this.userInfo, autolock: enabled ? 1 : 0 }
    },
    setAllLock(enabled: boolean) {
      this.userInfo = { ...this.userInfo, allLock: enabled ? 1 : 0 }
    },
    /**
     * 登录失效时使用：
     * 只清空 userid / token / 登录标识
     * 保留 uuid、playing、游客设备信息
     */
    clearAuth() {
      this.userInfo = {
        ...this.userInfo,
        id: '',
        token: '',
        islogin: 0,
      }
    },
    /**
     * 完全重置用户信息。
     * 注意：不清 uuid，因为 Web 端游客身份依赖 uuid。
     */
    resetUserInfo() {
      this.userInfo = defaultUserInfo()
      this.isLogin = false
    },
    /**
     * 主动退出登录时使用。
     * 如果你希望退出后保留观看缓存，可以不要清 playing。
     */
      logout() {
        this.userInfo = defaultUserInfo()
        this.isLogin = false
        this.playing = {}
      }
  },
  persist: {
    key: 'user',
    omit: ['isLogin', 'quality']
  },
})
